/**
 * HorizontalStepper - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * Lime accents for current step, white for completed
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

export interface StepperStep {
  id: string;
  label: string;
  number: number;
  status: 'completed' | 'current' | 'upcoming';
}

interface HorizontalStepperProps {
  steps: StepperStep[];
  currentStep: number;
  totalSteps: number;
  className?: string;
}

export const HorizontalStepper: React.FC<HorizontalStepperProps> = ({
  steps,
  currentStep,
  totalSteps,
  className = '',
}) => {
  const { t } = useTranslation();
  const percentage = Math.round((currentStep / totalSteps) * 100);

  return (
    <div className={className}>
      {/* Progress header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-lime-500/20 backdrop-blur-sm border border-lime-400/30 flex items-center justify-center">
            <span className="text-lime-400 font-bold text-lg">{currentStep}</span>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-white/50">
              {t('onboarding.stepper.stepOf', { current: currentStep, total: totalSteps })}
            </p>
            <p className="text-white font-semibold">{t('onboarding.stepper.accountSetup')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
            <div
              className="h-full bg-gradient-to-r from-lime-400 to-lime-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className="text-lime-400 font-bold text-sm">{percentage}%</span>
        </div>
      </div>

      {/* Steps row */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {steps.map((step, index) => {
          const isCompleted = step.status === 'completed';
          const isCurrent = step.status === 'current';
          const isUpcoming = step.status === 'upcoming';

          return (
            <React.Fragment key={step.id}>
              {/* Step indicator */}
              <div
                className={`flex items-center gap-3 flex-shrink-0 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isCurrent
                    ? 'bg-lime-500/20 backdrop-blur-sm border border-lime-400/40'
                    : isCompleted
                    ? 'bg-white/10 backdrop-blur-sm border border-white/20'
                    : 'bg-white/5 backdrop-blur-sm border border-white/10'
                }`}
              >
                {isCompleted ? (
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-lime-400 to-lime-500 text-teal-800 text-sm flex items-center justify-center font-bold shadow-lg shadow-lime-500/20 transition-all">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </div>
                ) : isCurrent ? (
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-lime-400 to-lime-500 text-teal-800 text-sm flex items-center justify-center font-bold shadow-lg shadow-lime-500/30 animate-pulse transition-all">
                    {step.number}
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-xl border-2 border-white/20 bg-white/5 text-white/40 text-sm flex items-center justify-center font-bold transition-all">
                    {step.number}
                  </div>
                )}

                <span
                  className={`text-sm font-medium whitespace-nowrap transition-colors ${
                    isCurrent
                      ? 'text-lime-400 font-semibold'
                      : isCompleted
                      ? 'text-white'
                      : 'text-white/40'
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {/* Connecting line */}
              {index < steps.length - 1 && (
                <div
                  className={`h-0.5 w-6 flex-shrink-0 transition-all duration-500 rounded-full ${
                    isCompleted
                      ? 'bg-gradient-to-r from-lime-400 to-lime-500'
                      : 'bg-white/10'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
